import { AgencyDetail, AgencyRebate, Currency, Language, TimeZone } from 'core';
import { AdLogoType } from 'core/adLogo/AdLogo';
import { CHANNEL_GROUP_ID } from 'core/channelGroup/ChannelGroupSettings';
import moment from 'moment';
import { toOptions } from 'utils/EnumUtil';

/**
 * Default campaign limitation values.
 *
 * Data shape (frontend-driven):
 *   addonFeatures.campaignLimitation[`channel_${channelGroupId}_${featureKey}`] = boolean
 *
 * RTB is the only channel add-on enabled by default (see
 * `defaultAgency.addonFeatures.channel`), and the form only renders a section
 * for a channel group whose channel add-on is on — so the default only needs
 * the RTB bucket. `RTB_DEFAULT_ENABLED_KEYS` are the keys enabled (true) by
 * default; any other (EdiMax / outdoor) channel group starts absent and its
 * switches default to off until that channel add-on is enabled.
 */
const RTB_DEFAULT_ENABLED_KEYS = [
  'country',
  'spaceType',
  'device',
  'target_audience',
  'adexchange',
  'domain',
  'city',
  'enablePMPDeal'
];

const buildCampaignLimitationDefaults = (): {
  [key: string]: boolean;
} =>
  RTB_DEFAULT_ENABLED_KEYS.reduce<{ [key: string]: boolean }>((acc, key) => {
    acc[`channel_${CHANNEL_GROUP_ID.RTB}_${key}`] = true;
    return acc;
  }, {});

const defaultAgency: AgencyDetail & AgencyRebate = {
  addonFeatures: {
    channel: {
      openRTB: true
    },
    creatives: {
      option_1: true,
      option_2: false,
      option_3: false,
      option_4: false,
      option_5: false,
      option_7: false,
      option_8: false,
      option_9: false,
      option_10: false,
      option_11: false,
      option_20: true,
      option_21: true,
      option_22: false,
      customizeAdLogo: true
    },
    company: {
      rebate: false,
      allow: false,
      singleOrderBudgetConstraint: false,
      productGroup: false,
      orderBudgetMaximum: false,
      useAgencyProfitOnRebateReport: false,
      showOutComeOnRebateReport: false,
      creditLimit: false,
      allowOrderSettlement: false,
      fixedExchangeRate: false,
      agencyProfit: false,
      advertiserViewSpentInReport: true,
      provideReportToWorkflow: false,
      storedValue: false,
      advAdminInfoRestriction: false,
      directlyApproveCreative: false
    },
    order: {
      showOrderExternalType: false
    },
    campaign: {
      // 全域旗標。OtherSettings 相關旗標 + 計價模式 root-level 影本均保留：
      // 計價模式 root-level keys 由 legacy `isFeatureEnable(CAMPAIGN.<KEY>)`
      // 讀取，per-channel keys 由 `isFeatureEnable(perChannelCampaignKey(id, key))`
      // 讀取。兩個 path 同時存在，AddonFeatureManager 依 key 形式 dispatch。
      bid_weight_setting: false,
      deliveryAmountConstraint: false,
      bidPriceConstraint: false,
      bidPriceBaseVal: false,
      bidPriceCPCMin: false,
      bidPriceCPMMin: false,
      bidPriceCPVMin: false,
      revenueSharing: false,
      budgetDominate: false,
      roadblock: false,
      videoAdViewObjective: false,
      thirdparty_tracking: false,
      retargeting_tracker: false,
      enableOptimizedPrice: false,
      orderAgcProfitSetting: false,
      dynamic_cpm: true,
      // Per-channel pricing-mode 旗標以 flat composite key `channel_${id}_${key}`
      // 儲存於 campaign root，與 legacy 全域 flag 共存。預設保留 dynamic_cpm=true
      // 與 (master) 行為一致；其餘 7 個 pricing mode 預設由使用者依需要開啟。
      // 0 = RTB 是 default `channel` 列出的 static channel；
      // 1 是 EdiMax，它已改由後端資料驅動且渠道停用，這一筆留著只是讓既有經銷商的
      // 旗標結構不變，其餘 dynamic channel 由 BE 於建立時補齊。
      channel_0_dynamic_cpm: true,
      channel_1_dynamic_cpm: true
    },
    creative_studio: {
      creative_studio: false
    },
    product: {
      index: false
    },
    conversiontracking: {
      TrackingList: false
    },
    report: {
      viewableCtr: false,
      campaign_dashboard: false,
      booking_calendar: false,
      yahooAd: false,
      closingReport: false,
      restrictFinalReport: false,
      campaignDashboard: false
    },
    monitor: {
      campaignBehindProgress: false
    },
    limitation: {
      country: false,
      spaceType: false,
      device: false,
      target_audience: false,
      adexchange: false,
      asiamax_space: false,
      domain: false,
      space_category: false,
      tenmax_audience: false,
      product_segment: false,
      city: false,
      gender: false,
      age: false,
      carrier: false,
      enablePMPDeal: false,
      keyword: false,
      unicornkeywords: false,
      content_language: false,
      content_category: false
    },
    campaignLimitation: buildCampaignLimitationDefaults()
  },
  addonProps: {
    maxOrderProfit: 0,
    orderBudgetMaximum: 10000,
    creditLimit: 0,
    fixedExchangeRate: 0,
    campaignBidPrice: [],
    channelMargin: {},
    expressPass: {}
  },
  bidPriceBase: {
    DISPLAY: 1,
    COMBO: 17,
    VIDEO: 90,
    THIRD_PARTY: 112.5,
    THIRD_PARTY_BOTTOM: 112.5,
    THIRD_PARTY_RECTANGLE: 112.5
  },
  logoUrl: '',
  agcDataPercent: 0,
  agcPercent: 0,
  agencyId: 0,
  comComment: '',
  companyName: '',
  currency: Currency.NTD,
  defaultCountry: [],
  defaultCountryOp: 'inc',
  defaultSpace: [],
  defaultSpaceOp: 'inc',
  interstitial: 0,
  oem: false,
  language: Language.ZHTW,
  limitAdx: [
    {
      label: 'DoubleClick',
      value: 'DoubleClick'
    },
    {
      label: 'AsiaMax',
      value: 'AsiaMax'
    },
    {
      label: 'BidSwitch',
      value: 'BidSwitch'
    }
  ],
  limitAdxOp: 'inc',
  limitPublisher: [],
  limitPublisherCountry: [],
  noLimitSelfBuyStatus: false,
  priority: 1,
  segmentCountry: [],
  segmentLabel: [],
  selfBuy: false,
  sysDataPercent: 0,
  sysPercent: 0,
  sysPercentIn: 0,
  sysPercentOut: 0,
  targetBudgetMinimum: 0,
  campaignBudgetMinimum: 0,
  taxPercent: 0,
  timeZone: TimeZone.TAIPEI,
  adLogo: {
    type: AdLogoType.NULL
  },
  logoData: {},
  currentRate: 0,
  futureRate: 0,
  effectDate: moment().startOf('day').format('YYYY-MM-DD')
};

const data = {
  defaultAgency,
  timeZoneOptions: toOptions(TimeZone),
  currencyOptions: toOptions(Currency),
  languageOptions: toOptions(Language)
};

export default data;
